import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  Skeleton,
  Drawer,
  Divider,
  Paper,
  Tooltip,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { listRequests, getRequestDetail } from '../api/dashboard';
import { REQUEST_TYPE_COLORS } from '../theme';
import type { RequestSummary, RequestDetail, RequestType } from '../types';
import TurnKindChip from './agents/TurnKindChip';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  workflow: 'Workflow',
  workflow_plan: 'Plan',
  query: 'Query',
  agent: 'Agent',
  checklist: 'Checklist',
  compare: 'Compare',
  other: 'Other',
};

interface UserRequestsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  requestType: 'query' | 'workflow';
  requestCount: number;
}

export default function UserRequestsModal({
  open,
  onClose,
  userId,
  userName,
  requestType,
  requestCount,
}: UserRequestsModalProps) {
  const [requests, setRequests] = useState<RequestSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestDetail | null>(null);
  const [isDetailOpen, setDetailOpen] = useState(false);
  const [isLoadingDetail, setLoadingDetail] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const handleCopy = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbar({ open: true, message: `${label} copied!` });
    } catch {
      setCopySnackbar({ open: true, message: 'Failed to copy' });
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    if (!open || !userId) return;

    setIsLoading(true);
    try {
      // Fetch last 6 months of requests for this user (backend uses optimized single query)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 180);

      // Map to llm_traces request types
      const types = requestType === 'workflow' ? ['workflow', 'workflow_plan'] : [requestType];

      const response = await listRequests(
        startDate,
        endDate,
        1, // page
        200, // pageSize - Limit to 200 requests
        'desc',
        {
          userIds: [userId],
          requestTypes: types,
        }
      );
      setRequests(response.requests);
    } catch (error) {
      console.error('Failed to fetch user requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [open, userId, requestType]);

  useEffect(() => {
    if (open) {
      fetchRequests();
    } else {
      // Reset state when closing
      setRequests([]);
      setSelectedRequest(null);
      setDetailOpen(false);
    }
  }, [open, fetchRequests]);

  const handleRequestClick = async (request: RequestSummary) => {
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const detail = await getRequestDetail(request.traceId);
      setSelectedRequest(detail);
    } catch (error) {
      console.error('Failed to fetch request detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const typeLabel = requestType === 'query' ? 'Queries' : 'Workflows';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: 700,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" component="span">
              {userName}'s {typeLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {requestCount} total • Showing up to 200 most recent (last 6 months)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={fetchRequests}
              disabled={isLoading}
              sx={{ color: 'text.secondary' }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={100} />
              ))}
            </Box>
          ) : requests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">
                No {typeLabel.toLowerCase()} found for this user
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {requests.map((request) => (
                <Card
                  key={request.traceId}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => handleRequestClick(request)}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header row with badges */}
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={REQUEST_TYPE_LABELS[request.requestType]}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: REQUEST_TYPE_COLORS[request.requestType] + '25',
                          color: REQUEST_TYPE_COLORS[request.requestType],
                          fontWeight: 600,
                        }}
                      />
                      <TurnKindChip kind={request.turnKind} />
                      {request.isFollowup && (
                        <Chip
                          label="FollowUp"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(59, 130, 246, 0.25)',
                            color: '#3b82f6',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {request.templateName && (
                        <Chip
                          label="Template"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(168, 85, 247, 0.25)',
                            color: '#a855f7',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {(request.orgName || request.orgId) && (
                        <Chip
                          label={request.orgName || request.orgId?.slice(0, 8) + '...'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(request.timestamp), 'MMM d, yyyy HH:mm')}
                      </Typography>
                      {request.successStatus !== null && (
                        request.successStatus ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />
                        )
                      )}
                      {request.latency !== null && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          {request.latency < 1
                            ? `${(request.latency * 1000).toFixed(0)}ms`
                            : `${request.latency.toFixed(1)}s`}
                        </Typography>
                      )}
                    </Box>

                    {/* Request text */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        bgcolor: 'rgba(0,0,0,0.15)',
                        p: 1,
                        borderRadius: 0.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 80,
                        overflow: 'hidden',
                      }}
                    >
                      {request.userRequestPreview || request.userRequest || 'No request text available'}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 480 } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Request Details</Typography>
            <IconButton size="small" onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {isLoadingDetail ? (
            <Skeleton variant="rounded" height={400} />
          ) : selectedRequest ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={REQUEST_TYPE_LABELS[selectedRequest.requestType]}
                  size="small"
                  sx={{
                    bgcolor: REQUEST_TYPE_COLORS[selectedRequest.requestType] + '25',
                    color: REQUEST_TYPE_COLORS[selectedRequest.requestType],
                  }}
                />
                <TurnKindChip kind={selectedRequest.turnKind} />
                {selectedRequest.isFollowup && (
                  <Chip
                    label="FollowUp"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.25)',
                      color: '#3b82f6',
                    }}
                  />
                )}
                {selectedRequest.templateName && (
                  <Chip
                    label={`Template: ${selectedRequest.templateName}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(168, 85, 247, 0.25)',
                      color: '#a855f7',
                    }}
                  />
                )}
                <Chip label={selectedRequest.env || 'unknown'} size="small" variant="outlined" />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                  <Typography variant="body2">
                    {format(new Date(selectedRequest.timestamp), 'PPpp')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Latency</Typography>
                  <Typography variant="body2">
                    {selectedRequest.latency !== null
                      ? selectedRequest.latency < 1
                        ? `${(selectedRequest.latency * 1000).toFixed(0)}ms`
                        : `${selectedRequest.latency.toFixed(2)}s`
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Organization</Typography>
                  <Typography variant="body2">
                    {selectedRequest.orgName || selectedRequest.orgId || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">
                      {selectedRequest.userName || selectedRequest.userId || 'N/A'}
                    </Typography>
                    {(selectedRequest.userName || selectedRequest.userId) && (
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(selectedRequest.userName || selectedRequest.userId || '', 'User')}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Project</Typography>
                  <Typography variant="body2">
                    {selectedRequest.projectName || selectedRequest.projectId || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2">
                    {selectedRequest.successStatus === null
                      ? 'Unknown'
                      : selectedRequest.successStatus
                        ? '✓ Success'
                        : '✗ Failed'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">User Request</Typography>
              <Paper
                sx={{
                  p: 1.5,
                  mt: 0.5,
                  bgcolor: 'rgba(0,0,0,0.2)',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 250,
                  overflow: 'auto',
                }}
              >
                {selectedRequest.userRequest || 'No request text available'}
              </Paper>

              {selectedRequest.mentions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Mentions ({selectedRequest.mentions.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {selectedRequest.mentions.map((mention, i) => (
                      <Chip key={i} label={mention} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Trace ID</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {selectedRequest.traceId}
                  </Typography>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedRequest.traceId, 'Trace ID')}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
